import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import Logo from "../../components/Logo";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        const res = await login(email, password);
        setLoading(false);
        if (!res.ok) {
            setError(res.error);
            return;
        }
        const dest = location.state?.from || (res.user.role === "admin" ? "/admin" : "/dashboard");
        navigate(dest, { replace: true });
    };

    return (
        <div className="min-h-screen flex flex-col lg:flex-row" data-testid="login-page">
            <aside className="lg:w-1/2 relative overflow-hidden border-b lg:border-b-0 lg:border-r border-avx-line p-10 lg:p-16 flex flex-col justify-between min-h-[260px]">
                <div className="absolute inset-0 opacity-30">
                    <img src="https://images.unsplash.com/photo-1552174588-6733961c358e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="" className="w-full h-full object-cover" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-br from-avx-bg/80 to-avx-bg" />
                <div className="relative">
                    <Logo testId="login-logo" />
                </div>
                <div className="relative max-w-md">
                    <div className="label-mono mb-3">/ Private access</div>
                    <h2 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter leading-tight">
                        Your <span className="text-avx-blue">professional</span> plugins, ready for production.
                    </h2>
                </div>
            </aside>
            <main className="lg:w-1/2 flex items-center justify-center p-8 lg:p-16">
                <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
                    <div className="label-mono mb-2">/ Log in</div>
                    <h1 className="font-heading font-black text-3xl tracking-tighter mb-2">Welcome back</h1>
                    <p className="text-sm text-avx-muted mb-8">Enter your credentials to access your dashboard.</p>

                    {error && (
                        <div className="mb-4 p-3 border border-avx-danger/40 bg-avx-danger/10 text-avx-danger text-sm rounded-sm" data-testid="login-error">
                            {error}
                        </div>
                    )}

                    <div className="space-y-4 mb-6">
                        <div>
                            <Label htmlFor="email" className="label-mono">Email</Label>
                            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input"
                                className="mt-2 bg-avx-surface border-avx-line h-11 focus:border-avx-blue focus:ring-1 focus:ring-avx-blue" />
                        </div>
                        <div>
                            <Label htmlFor="password" className="label-mono">Password</Label>
                            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input"
                                className="mt-2 bg-avx-surface border-avx-line h-11 focus:border-avx-blue focus:ring-1 focus:ring-avx-blue" />
                        </div>
                    </div>

                    <button type="submit" disabled={loading} data-testid="login-submit-btn"
                        className="btn-primary w-full py-3 text-xs font-mono uppercase tracking-[0.2em] rounded-sm">
                        {loading ? "Logging in…" : "Log in"}
                    </button>

                    <div className="mt-4 text-center">
                        <Link to="/forgot-password" className="text-xs text-avx-muted hover:text-avx-blue font-mono uppercase tracking-[0.18em]" data-testid="login-forgot-link">
                            Forgot your password?
                        </Link>
                    </div>

                    <div className="mt-6 flex justify-between items-center text-xs">
                        <Link to="/register" className="text-avx-muted hover:text-avx-blue font-mono uppercase tracking-[0.18em]" data-testid="login-register-link">
                            Create account →
                        </Link>
                        <Link to="/" className="text-avx-muted hover:text-avx-blue font-mono uppercase tracking-[0.18em]">
                            Back to home
                        </Link>
                    </div>
                </form>
            </main>
        </div>
    );
}
